/**
 * city/street/street.js
Dựng hạ tầng đường phố: Mặt đường nhựa, vỉa hè, bó vỉa, cột điện ly tâm chằng chịt
 */

import * as THREE from 'three';
import { createRng } from '../builder-core.js';

class MethodsHolder {
  createPowerPole(options = {}) {
    const pole = new THREE.Group();
    pole.name = 'VietnamPowerPole';
    const span = options.span ?? 9;
    const rnd = createRng(options.seed ?? 7);

    const concrete = this.mat('#a8a29e', { roughness: 0.95 });
    const metal = this.mat('#52525b', { roughness: 0.45, metalness: 0.6 });
    const porcelain = this.mat('#f5f5f4', { roughness: 0.3 });
    const cable = this.mat('#111111', { roughness: 0.6 });

    this.box(pole, 0.55, 0.2, 0.55, this.mat('#78716c', { roughness: 0.95 }), 0, 0.1, 0);
    this.cyl(pole, 0.12, 0.2, 7.9, 12, concrete, 0, 3.95, 0);

    // Xà ngang VUÔNG GÓC hướng dây (dây chạy dọc phố theo trục X)
    this.box(pole, 0.1, 0.1, 1.8, metal, 0, 7.6, 0);
    const phases = [
      [-0.75, 7.72],
      [0.75, 7.72],
      [0, 7.96]
    ];
    phases.forEach(([pz, py]) => {
      this.cyl(pole, 0.05, 0.065, 0.08, 8, porcelain, 0, py - 0.03, pz);
      this.cyl(pole, 0.065, 0.065, 0.04, 8, porcelain, 0, py + 0.03, pz);
      this.cyl(pole, 0.045, 0.05, 0.06, 8, porcelain, 0, py + 0.08, pz);
    });
    phases.forEach(([pz, py]) =>
      [-1, 1].forEach((dir) => {
        this._sagWire(
          pole,
          new THREE.Vector3(0, py + 0.1, pz),
          new THREE.Vector3(dir * span, py + 0.1, pz),
          0.35,
          0.014,
          cable
        );
      })
    );

    // Cáp viễn thông võng thấp nhiều sợi
    for (let i = 0; i < 6; i++) {
      const y = 5.6 + rnd() * 0.5;
      const z = (rnd() - 0.5) * 0.35;
      const r = 0.012 + rnd() * 0.014;
      [-1, 1].forEach((dir) =>
        this._sagWire(
          pole,
          new THREE.Vector3(0, y, z),
          new THREE.Vector3(dir * span, y, z + (rnd() - 0.5) * 0.3),
          0.4 + rnd() * 0.6,
          r,
          cable
        )
      );
    }
    // Dây kéo vào nhà dân phía sau vỉa hè
    for (let i = 0; i < 3; i++) {
      this._sagWire(
        pole,
        new THREE.Vector3(0, 5.5 + rnd() * 0.3, 0),
        new THREE.Vector3((rnd() - 0.5) * 2.5, 4.3 + rnd() * 0.5, -1.2),
        0.25,
        0.01,
        cable
      );
    }

    // BÚI DÂY CUỘN chằng chịt & hộp cáp quang
    for (let i = 0; i < 4; i++) {
      const tr = 0.22 + rnd() * 0.14;
      const geo = this._geo(`torus|${tr.toFixed(2)}`, () => new THREE.TorusGeometry(tr, 0.028, 6, 18));
      const coil = new THREE.Mesh(geo, cable);
      coil.position.set((rnd() - 0.5) * 0.3, 4.9 + i * 0.16, (rnd() - 0.5) * 0.3);
      coil.rotation.set(Math.PI / 2 + (rnd() - 0.5) * 0.8, rnd() * 0.5, (rnd() - 0.5) * 0.6);
      coil.castShadow = true;
      pole.add(coil);
    }
    [0.35, -0.35].forEach((ox, i) => {
      const loop = new THREE.Mesh(
        this._geo('torus|loop', () => new THREE.TorusGeometry(0.3, 0.02, 6, 18)),
        cable
      );
      loop.position.set(ox, 4.4 - i * 0.2, 0.05);
      loop.rotation.y = 0.3;
      pole.add(loop);
    });
    this.box(pole, 0.26, 0.36, 0.14, this.mat('#d4d4d8', { roughness: 0.6 }), 0, 4.55, 0.22);
    this.box(pole, 0.2, 0.28, 0.12, this.mat('#e7e5e4', { roughness: 0.6 }), 0.02, 5.0, -0.22);
    [4.25, 4.8, 5.35].forEach((by) => this.cyl(pole, 0.2, 0.2, 0.035, 12, cable, 0, by, 0, null, false));

    // Hộp công tơ điện
    [
      [2.9, 0.24],
      [3.4, 0.24],
      [3.15, -0.24]
    ].forEach(([my, mz]) => {
      this.box(pole, 0.34, 0.44, 0.16, this.mat('#9ca3af', { roughness: 0.55, metalness: 0.3 }), 0, my, mz);
      this.box(
        pole,
        0.16,
        0.1,
        0.01,
        this.mat('#1e293b', { roughness: 0.2 }),
        0,
        my + 0.08,
        mz + Math.sign(mz) * 0.085,
        null,
        false
      );
    });

    // Cần đèn đường vươn ra lòng đường (+Z)
    this.box(pole, 0.06, 0.06, 1.6, metal, 0, 6.55, 0.8, [-0.18, 0, 0]);
    this.box(pole, 0.2, 0.08, 0.5, metal, 0, 6.72, 1.72);
    this.box(pole, 0.16, 0.02, 0.4, this.glow('#fef9c3'), 0, 6.67, 1.72, null, false);

    // Giấy quảng cáo rao vặt dán cột ("khoan cắt bê tông", "hút bể phốt"...)
    const stickerColors = ['#fef08a', '#ffffff', '#fbcfe8', '#bbf7d0', '#fed7aa'];
    for (let i = 0; i < 7; i++) {
      const a = rnd() * Math.PI * 2;
      const y = 1.2 + rnd() * 1.2;
      const r = 0.2 - 0.08 * (y / 7.9) + 0.004;
      this.box(
        pole,
        0.11,
        0.15,
        0.004,
        this.mat(stickerColors[i % stickerColors.length], { roughness: 0.9 }),
        Math.sin(a) * r,
        y,
        Math.cos(a) * r,
        [0, a, 0],
        false
      );
    }
    return pole;
  }

  // MẶT ĐƯỜNG PHỐ HOÀN CHỈNH: nhựa, vạch kẻ, vạch sang đường, bó vỉa, vỉa hè gạch, cống thoát nước
  createStreet(options = {}) {
    const street = new THREE.Group();
    street.name = 'VietnamStreet';
    const length = options.length ?? 36;
    const roadW = options.roadWidth ?? 7;
    const walkW = options.sidewalkWidth ?? 5.25;
    const curbH = options.curbHeight ?? 0.22;
    const crossX = options.crosswalkX ?? 7.5;
    const halfRoad = roadW / 2;

    // Mặt đường nhựa
    const asphalt = this.texMat(this._repeat(this.getAsphaltTexture(), length / 4, roadW / 4), { roughness: 0.95 });
    this.plane(street, length, roadW, asphalt, 0, 0, 0, [-Math.PI / 2, 0, 0]);
    // Rãnh biên bê tông sát bó vỉa
    [-1, 1].forEach((s) =>
      this.plane(street, length, 0.35, this.mat('#57534e', { roughness: 0.95 }), 0, 0.004, s * (halfRoad - 0.175), [
        -Math.PI / 2,
        0,
        0
      ])
    );

    // Vạch tim đường vàng đứt quãng (tránh vùng vạch sang đường) & vạch biên trắng liền
    const yellow = this.mat('#facc15', { roughness: 0.7 });
    const white = this.mat('#f5f5f4', { roughness: 0.7 });
    for (let x = -length / 2 + 1; x < length / 2 - 1; x += 4) {
      if (Math.abs(x - crossX) < 2.2) continue;
      this.plane(street, 2.0, 0.15, yellow, x, 0.01, 0, [-Math.PI / 2, 0, 0]);
    }
    [-1, 1].forEach((s) =>
      this.plane(street, length, 0.12, white, 0, 0.01, s * (halfRoad - 0.5), [-Math.PI / 2, 0, 0])
    );

    // Vạch sang đường (zebra): vạch song song chiều xe chạy
    for (let z = -halfRoad + 0.6; z < halfRoad - 0.4; z += 0.9) {
      this.plane(street, 3.0, 0.45, white, crossX, 0.012, z + 0.225, [-Math.PI / 2, 0, 0]);
    }
    // Vạch dừng xe trước vạch sang đường
    this.plane(street, 0.3, halfRoad - 0.5, white, crossX - 2.2, 0.012, (halfRoad - 0.5) / 2, [-Math.PI / 2, 0, 0]);
    this.plane(street, 0.3, halfRoad - 0.5, white, crossX + 2.2, 0.012, -(halfRoad - 0.5) / 2, [-Math.PI / 2, 0, 0]);

    // Vỉa hè gạch terrazzo + bó vỉa đá granite
    const tileTop = this.texMat(this._repeat(this.getSidewalkTexture(), length / 2, walkW / 2), { roughness: 0.9 });
    const walkSide = this.mat('#78716c', { roughness: 0.95 });
    const curbMat = this.texMat(this._repeat(this.getCurbTexture(), length / 2, 1), { roughness: 0.8 });
    [-1, 1].forEach((s) => {
      this.box(
        street,
        length,
        curbH,
        walkW,
        [walkSide, walkSide, tileTop, walkSide, walkSide, walkSide],
        0,
        curbH / 2,
        s * (halfRoad + walkW / 2),
        null,
        false
      );
      this.box(street, length, curbH + 0.02, 0.26, curbMat, 0, (curbH + 0.02) / 2, s * (halfRoad + 0.13));
      // Dốc dắt xe tại vạch sang đường
      this.box(
        street,
        2.6,
        0.02,
        0.5,
        this.mat('#a8a29e', { roughness: 0.9 }),
        crossX,
        curbH + 0.01,
        s * (halfRoad + 0.5),
        null,
        false
      );
    });

    // Hố ga thu nước sát bó vỉa & nắp cống tròn giữa đường
    const grate = this.texMat(this.getGrateTexture(), { roughness: 0.8, metalness: 0.4 });
    for (let x = -length / 2 + 3; x < length / 2; x += 9) {
      [-1, 1].forEach((s) =>
        this.box(street, 0.8, 0.02, 0.32, grate, x + s * 1.5, 0.01, s * (halfRoad - 0.2), null, false)
      );
    }
    const manhole = this.texMat(this.getManholeTexture(), { roughness: 0.7, metalness: 0.4 });
    const edge = this.mat('#27272a', { roughness: 0.8 });
    [
      [-4.5, 1.1],
      [11, -1.2]
    ].forEach(([mx, mz]) => this.cyl(street, 0.36, 0.36, 0.02, 20, [edge, manhole, edge], mx, 0.01, mz, null, false));

    street.userData = { roadWidth: roadW, sidewalkWidth: walkW, sidewalkTopY: curbH, crosswalkX: crossX };
    return street;
  }
}

export function registerStreetMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
